import { Section } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import {
  isEmployee,
  isEmploymentOther,
  isExistingCommercial,
  isHomeowner,
  isRenting,
  isRetired,
  isSelfEmployed,
} from 'app/models/fields/conditionals';
import { ConfirmationCommercial } from 'app/components/ConfirmationCommercial';
import { getCommercialDeclarationQuestions } from '../questions/declarations';
import {
  COMMERCIAL_VEHICLE_LOAN_INFO,
  COMMERCIAL_VEHICLE_BUSINESS_INFO,
  COMMERCIAL_VEHICLE_BUSINESS_FINANCIALS,
  COMMERCIAL_VEHICLE_BUSINESS_MANAGEMENT,
  COMMERCIAL_VEHICLE_LOAN_HOLDER,
  COMMERCIAL_VEHICLE_PERSONAL_INFO,
  COMMERCIAL_VEHICLE_BORROWER_LIVING_SITUATION,
  COMMERCIAL_VEHICLE_BORROWER_HOME_INFO,
  COMMERCIAL_VEHICLE_BORROWER_RENTER_INFO,
  COMMERCIAL_VEHICLE_BORROWER_INCOME_SOURCE,
  COMMERCIAL_VEHICLE_BORROWER_EMPLOYMENT,
  COMMERCIAL_VEHICLE_BORROWER_RETIREMENT,
  COMMERCIAL_VEHICLE_BORROWER_QUESTIONS,
  COMMERCIAL_VEHICLE_FINAL,
  COMMERCIAL_VEHICLE_BUSINESS_OWNERS,
  COMMERCIAL_VEHICLE_EXISTING_CUSTOMER,
  COMMERCIAL_VEHICLE_EXISTING_CUSTOMER_INFO,
} from 'app/models/sections/constants';
import {
  getCommercialVehicleQuestionsByName,
} from '../questions/commercial-vehicle';
import { FormTerms } from '../../components/FormAgreements/FormTerms';
import React from 'react';
import { handleCommercialBackButton, handleCommercialFormSubmit } from 'app/actions/form/actions';
import { getCommercialBridgeLoanQuestionsByName } from '../questions/commercial-bridge-loan';
import { BorrowerType } from '@lenderful/domain';
import { ajaxValidateBorrowerResidence } from 'app/actions/form/address-validation/actions';
import { Disclosure } from 'app/components/Disclaimer/Disclosure';

export const getCommercialVehicleSectionById = (id: number): Section => {
  return COMMERCIAL_VEHICLE_SECTIONS.find((section) => section.id === id) as Section;
};

const COMMERCIAL_VEHICLE_SECTIONS: Section[] = [
  {
    id        : COMMERCIAL_VEHICLE_LOAN_INFO,
    title     : 'Tell us more about your vehicle purchase',
    questions : getCommercialVehicleQuestionsByName([
      FieldNames.commercialVehicleList,
    ]),
    agreement: <FormTerms />,
    hideBackButton: handleCommercialBackButton,
  },
  {
    id             : COMMERCIAL_VEHICLE_EXISTING_CUSTOMER,
    questions      : getCommercialVehicleQuestionsByName([
      FieldNames.isCommercialExistingCustomer,
    ]),
  },
  {
    id        : COMMERCIAL_VEHICLE_BUSINESS_INFO,
    title     : 'Tell us about your business',
    questions : getCommercialVehicleQuestionsByName([
      FieldNames.commercialBusinessName,
      FieldNames.commercialDBA,
      FieldNames.commercialCensusTract,
      FieldNames.commercialNAICSCode,
      FieldNames.commercialStreet,
      FieldNames.commercialStreet2,
      FieldNames.commercialCity,
      FieldNames.commercialState,
      FieldNames.commercialZip,
      FieldNames.commercialPhone,
      FieldNames.commercialEmployeeCount,
      FieldNames.commercialWebsite,
      FieldNames.commercialEntityType,
      FieldNames.commercialBusinessType,
      FieldNames.commercialBusinessTypeOther,
      FieldNames.commercialStateOfIncorporation,
      FieldNames.commercialYearStarted,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_VEHICLE_BUSINESS_FINANCIALS,
    title     : 'Tell us about your business financials',
    questions : getCommercialVehicleQuestionsByName([
      FieldNames.commercialRevenueTrend3,
      FieldNames.commercialRevenueLastYear,
      FieldNames.commercialRevenueExplanation,
      FieldNames.commercialProfitTrend3,
      FieldNames.commercialProfitExplanation,
      FieldNames.commercialCollateralList,
      FieldNames.commercialBusinessOwnershipType,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_VEHICLE_BUSINESS_OWNERS,
    questions : getCommercialVehicleQuestionsByName([
      FieldNames.commercialOwnerList,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_VEHICLE_BUSINESS_MANAGEMENT,
    questions : getCommercialVehicleQuestionsByName([
      FieldNames.hasOtherManagement,
      FieldNames.commercialManagementList,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_VEHICLE_LOAN_HOLDER,
    questions : getCommercialVehicleQuestionsByName([
      FieldNames.commercialLoanHolder,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id       : COMMERCIAL_VEHICLE_PERSONAL_INFO ,
    title    : 'Enter your personal information',
    questions: getCommercialVehicleQuestionsByName([
      FieldNames.firstName,
      FieldNames.middleName,
      FieldNames.lastName,
      FieldNames.suffixName,
      FieldNames.email,
      FieldNames.phone,
      FieldNames.dateOfBirth,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_VEHICLE_BORROWER_LIVING_SITUATION,
    title     : 'What is your current housing situation?',
    questions : getCommercialVehicleQuestionsByName([FieldNames.livingRentOrOwn]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id       : COMMERCIAL_VEHICLE_BORROWER_HOME_INFO,
    title    : 'Provide your homeowner information',
    questions: getCommercialVehicleQuestionsByName([
      FieldNames.livingPropertyType,
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
    ]),
    showIf: [(values) => isHomeowner(values) && !isExistingCommercial(values)],
    submitAction : (formValues) => ajaxValidateBorrowerResidence(formValues),
  },
  {
    id        : COMMERCIAL_VEHICLE_BORROWER_RENTER_INFO,
    title     : 'Provide your renter information',
    questions : getCommercialVehicleQuestionsByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingMonthlyRentAmount,
    ]),
    showIf : [(values) => isRenting(values) && !isExistingCommercial(values)],
    submitAction : (formValues) => ajaxValidateBorrowerResidence(formValues),
  },
  {
    id        : COMMERCIAL_VEHICLE_BORROWER_INCOME_SOURCE,
    title     : 'What is your income source?',
    questions : getCommercialVehicleQuestionsByName([FieldNames.employmentType]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_VEHICLE_BORROWER_EMPLOYMENT,
    title     : 'Tell us about your income and employment',
    questions : getCommercialVehicleQuestionsByName([
      FieldNames.commercialEmploySameBusiness,
      FieldNames.employCompanyName,
      FieldNames.employStreet,
      FieldNames.employStreet2,
      FieldNames.employCity,
      FieldNames.employState,
      FieldNames.employZip,
      FieldNames.employTitle,
      FieldNames.employPhone,
      FieldNames.employStart,
      FieldNames.employBase,
      FieldNames.employOvertime,
      FieldNames.employCommission,
      FieldNames.employBonus,
      FieldNames.additionalIncome,
      FieldNames.employPrevName,
      FieldNames.employPrevStreet,
      FieldNames.employPrevStreet2,
      FieldNames.employPrevCity,
      FieldNames.employPrevState,
      FieldNames.employPrevZip,
      FieldNames.employPrevTitle,
      FieldNames.employPrevStart,
      FieldNames.employPrevEnd,
    ]),
    showIf       : [(values) => (isEmployee(values) || isSelfEmployed(values)) && !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_VEHICLE_BORROWER_RETIREMENT,
    questions : getCommercialVehicleQuestionsByName([
      FieldNames.retirementIncome,
      FieldNames.otherIncome,
    ]),
    showIf       : [(values) => (isRetired(values) ||  isEmploymentOther(values)) && !isExistingCommercial(values)],
  },
  {
    id           : COMMERCIAL_VEHICLE_EXISTING_CUSTOMER_INFO,
    title        : 'Please enter your Loan and Business Information',
    questions    : getCommercialBridgeLoanQuestionsByName([
      FieldNames.existingLoanNumber,
      FieldNames.commercialBusinessName,
      FieldNames.commercialStreet,
      FieldNames.commercialStreet2,
      FieldNames.commercialCity,
      FieldNames.commercialState,
      FieldNames.commercialZip,
      FieldNames.firstName,
      FieldNames.lastName,
      FieldNames.email,
      FieldNames.phone,
    ]),
    showIf       : [isExistingCommercial],
    submitAction : handleCommercialFormSubmit,
    submitText   : 'Submit',
  },
  {
    id           : COMMERCIAL_VEHICLE_BORROWER_QUESTIONS,
    title        : 'Borrower Questions',
    questions    : getCommercialDeclarationQuestions(BorrowerType.PRIMARY),
    showIf       : [(values) => !isExistingCommercial(values)],
    submitAction : handleCommercialFormSubmit,
    submitText   : 'Submit',
    agreement    : <Disclosure />,
  },
  {
    id              : COMMERCIAL_VEHICLE_FINAL,
    ifNoQuestions   : ConfirmationCommercial,
    hideProgressBar : true,
  },
];
